// MyFavorites — iter109 layout.
//
// Changes vs iter101:
//   • Per-card actions switched from the prominent 2-row RideAndQRButtons
//     to the shared compact 3-button row (CompactActionsRow). Phone-circle
//     goes through the iter108 ContactPopup.
//   • The square save badge in the top-right of each card now uses the
//     shared HeartButton component (breathing animation + "Save for later"
//     hint on first paint, instead of a static gold heart). Tapping it
//     toggles the favorite via the existing unsave handler.
//   • Distance label is now hidden when the user has no GPS fix or when
//     the haversine distance exceeds 50 km — prevents nonsense values
//     like "387.3 km · 4842 min walk" when the user is in another
//     country. Only "category · neighborhood" renders otherwise.
//
// Sources:
//   • Logged-in → GET /api/favorites + GET /api/businesses/:id per favorite.
//   • Guest     → guestStore.getSaved() filtered to target_type=business.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Loader2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, getMe } from "../lib/api";
import { BottomNav } from "../components/BottomNav";
import CompactActionsRow from "../components/CompactActionsRow";
import HeartButton from "../components/HeartButton";
import { useI18n } from "../lib/i18n";
import { haversineKm, fmtDistanceWalk } from "../lib/geo";
import { useGps } from "../lib/useGps";
import * as guestStore from "../lib/guestStorage";
import FilterPlacesSheet, { DEFAULT_FILTERS, countActiveFilters, applyFilters } from "../components/FilterPlacesSheet";
import { CategoryLabel } from "../components/CategoryLabel";

// iter109 — Distance is meaningless once the user is more than a short
// walk/ride away. Hide it above this threshold so the "387 km · 4842 min
// walk" string can't appear when the traveler is at home in another country.
const DISTANCE_MAX_KM = 50;

export default function MyFavorites() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { coords } = useGps();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(undefined);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await getMe();
      if (cancelled) return;
      setUser(me);
      if (!me) {
        const saved = guestStore.getSaved().filter((s) => s.target_type === "business");
        const rows = await Promise.all(saved.map((s) =>
          api.get(`/businesses/${s.target_id}`)
            .then((r) => ({ ...r.data, _savedAt: s.saved_at }))
            .catch(() => null),
        ));
        if (cancelled) return;
        setItems(rows.filter(Boolean).sort((a, b) => (b._savedAt || "").localeCompare(a._savedAt || "")));
        setLoading(false);
        return;
      }
      try {
        const { data: favs } = await api.get("/favorites");
        const bizFavs = (favs || []).filter((f) => f.target_type === "business");
        const rows = await Promise.all(bizFavs.map((f) =>
          api.get(`/businesses/${f.target_id}`)
            .then((r) => ({ ...r.data, _savedAt: f.created_at }))
            .catch(() => null),
        ));
        if (cancelled) return;
        setItems(rows.filter(Boolean).sort((a, b) => (b._savedAt || "").localeCompare(a._savedAt || "")));
      } catch { /* ignore */ }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const unsave = async (bid) => {
    try {
      if (user) await api.post("/favorites/toggle", { target_id: bid, target_type: "business" });
      else guestStore.toggleSaved(bid, "business");
      setItems((p) => p.filter((b) => b.id !== bid));
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
  };

  const visible = useMemo(() => applyFilters(items, filters, { coords }), [items, filters, coords]);
  const activeCount = countActiveFilters(filters);

  return (
    <main data-testid="my-favorites-page" className="min-h-screen pb-28" style={{ background: "#0B0B0B" }}>
      <header className="px-5 pt-6 pb-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} aria-label="Back" data-testid="my-favorites-back"
          className="grid place-items-center"
          style={{ minHeight: 48, minWidth: 48, marginLeft: -8 }}>
          <ArrowLeft className="w-5 h-5 text-foreground/80" strokeWidth={1.5} />
        </button>
        <h1 className="flex-1" style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: "#F5F1E8" }}>
          {t("page_my_favorites_title")}
        </h1>
        {/* iter133 — Filter button (shared FilterPlacesSheet) */}
        <button
          type="button"
          data-testid="my-favorites-filter-btn"
          aria-label="Open filters"
          onClick={() => setFilterOpen(true)}
          className="relative grid place-items-center rounded-full hover:bg-[rgba(232,184,75,0.08)] transition-colors"
          style={{
            width: 36,
            height: 36,
            border: "1px solid rgba(232,184,75,0.5)",
            color: "#E8B84B",
          }}
        >
          <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} />
          {activeCount > 0 && (
            <span
              data-testid="my-favorites-filter-badge"
              className="absolute"
              style={{
                top: -6,
                right: -6,
                minWidth: 18,
                height: 18,
                padding: "0 5px",
                background: "#E8B84B",
                color: "#1a0f06",
                borderRadius: 9999,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 11,
                fontWeight: 700,
                display: "grid",
                placeItems: "center",
              }}
            >
              {activeCount}
            </span>
          )}
        </button>
      </header>

      {loading ? (
        <div className="px-5 mt-10 text-center">
          <Loader2 className="w-5 h-5 animate-spin mx-auto text-foreground/40" />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={Heart}
          message={items.length === 0
            ? t("page_my_favorites_empty")
            : (t("page_my_favorites_filtered_empty") || "No favorites match your filters.")}
          testid="my-favorites-empty"
        />
      ) : (
        <ul className="px-5 mt-6 space-y-2.5">
          {visible.map((b) => (
            <li key={b.id} className="lux-card p-3" data-testid={`my-favorites-${b.id}`}>
              <div className="flex items-center gap-3">
                <Thumb url={b.photos?.[0]?.url} icon={Heart} />
                <div className="flex-1 min-w-0">
                  <Link to={`/places/${b.id}`} className="font-display text-base text-foreground truncate block">{b.name}</Link>
                  <div className="text-[11px] text-foreground/55 mt-0.5">
                    <CategoryLabel category={b.category} subcategory={b.subcategory} />
                    {b.neighborhood ? ` · ${b.neighborhood}` : ""}
                    {distanceLabel(coords, b)}
                  </div>
                </div>
                <HeartButton
                  saved={true}
                  onToggle={() => unsave(b.id)}
                  testid={`my-favorites-unsave-${b.id}`}
                  ariaLabel="Unsave"
                  showHint={false}
                />
              </div>
              {b.lat != null && b.lng != null && (
                <CompactActionsRow
                  destination={{
                    id: b.id,
                    name: b.name,
                    address: b.address,
                    lat: b.lat,
                    lng: b.lng,
                    category: b.category,
                    neighborhood: b.neighborhood,
                    phone: b.phone,
                    whatsapp: b.whatsapp,
                  }}
                  testidPrefix={`my-favorites-${b.id}`}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      <FilterPlacesSheet
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={setFilters}
        currentFilters={filters}
        businesses={items}
        resultCount={visible.length}
      />

      <BottomNav />
    </main>
  );
}

export function EmptyState({ icon: Icon, message, testid }) {
  return (
    <div className="px-8 mt-20 text-center" data-testid={testid}>
      <div className="mx-auto mb-4 grid place-items-center"
        style={{ width: 56, height: 56, borderRadius: 999, border: "1px solid rgba(232,184,75,0.35)" }}>
        <Icon className="w-6 h-6 text-gold" strokeWidth={1.5} />
      </div>
      <p className="text-foreground/70 text-[15px] leading-relaxed max-w-xs mx-auto"
        style={{ fontFamily: "'DM Sans', sans-serif" }}>
        {message}
      </p>
    </div>
  );
}

export function Thumb({ url, icon: Icon }) {
  return (
    <div className="w-[60px] h-[60px] rounded-lg overflow-hidden grid place-items-center flex-shrink-0"
      style={{ background: "#171717" }}>
      {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : (
        <Icon className="w-7 h-7 text-gold opacity-70" strokeWidth={1.5} />
      )}
    </div>
  );
}

function prettyCategory(c) {
  return (c || "").replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

function distanceLabel(coords, target) {
  if (!coords || target.lat == null || target.lng == null) return null;
  const km = haversineKm(coords, { lat: target.lat, lng: target.lng });
  if (km == null || km > DISTANCE_MAX_KM) return null;
  const s = fmtDistanceWalk(km);
  return s ? ` · ${s}` : null;
}
